// Role: Android Developer — Kotlin
// Tailored for Android/mobile development roles

export const portfolioData = {
  profile: {
    name: "MEERAIAH YAMPALLA",
    title: "Senior Software Engineer",
    yearsOfExperience: "22+",
    resumeFile: "sr-software",
    location: "Dallas, TX, USA",
    email: "yampallatech@gmail.com",
    phone: "***-***-9489",
    linkedin: "https://www.linkedin.com/in/ywer-2312-tpw/",
    github: "https://github.com/meeraiahy",
    summary: "Experienced Software Engineer with 20+ years in enterprise software development, including hands-on experience building and integrating mobile APIs, push notification services, and native mobile backends. Strong background in developing RESTful APIs and SDKs consumed by iOS and Android applications, with deep expertise in AWS cloud services, event-driven architecture, and secure API design. Bringing full-stack depth to Android development with Kotlin."
  },

  experience: [
    {
      id: 1, company: "Southwest Airlines", location: "Dallas, TX",
      title: "Senior Software Engineer", period: "Aug 2023 – Present",
      achievements: [
        "Developed native Android features using Kotlin and Android SDK, following MVVM architecture with LiveData, ViewModel, and Repository pattern for clean separation of concerns",
        "Built and maintained Android UI layouts using ConstraintLayout, RecyclerView, and ViewBinding — ensuring responsive, accessible designs across multiple screen sizes and densities",
        "Implemented Dependency Injection using Hilt (Dagger2) to manage component lifecycles, improve testability, and reduce boilerplate across the Android codebase",
        "Applied MVC and MVVM design patterns to structure Android modules, enabling clear separation between UI, business logic, and data layers",
        "Integrated Retrofit and OkHttp for REST API consumption, handling authentication flows with OAuth2/JWT and managing token refresh cycles within the Android app",
        "Used Android Studio for development, profiling, and debugging — leveraged Layout Inspector, Memory Profiler, and Network Inspector to diagnose and resolve performance bottlenecks",
        "Implemented push notification handling via Firebase Cloud Messaging (FCM) including foreground/background notification routing and deep-link navigation",
        "Developed and maintained shared Node.js/TypeScript SDKs and internal libraries consumed by mobile apps and web clients",
        "Designed and enforced REST API standards (OpenAPI/Swagger) ensuring mobile-friendly, consistent API contracts for Android and iOS clients",
        "Applied security-first principles: JWT authentication, OAuth2, secrets management, and ProGuard/R8 obfuscation for Android release builds",
        "Built observability into backend services supporting mobile apps using CloudWatch and Splunk; integrated Crashlytics for Android crash reporting and monitoring"
      ]
    },
    {
      id: 2, company: "SiriusXM", location: "Irving, TX",
      title: "Senior Software Consultant", period: "Apr 2019 – Aug 2023",
      achievements: [
        "Built backend microservices for Connected Vehicle platform consumed by native mobile apps (Toyota, Nissan, Lexus)",
        "Engineered user lifecycle management APIs — vehicle owner registration, subscription provisioning, and service tier management for mobile clients",
        "Developed real-time event pipelines using Kinesis processing telemetry from millions of connected vehicles",
        "Implemented secure REST APIs with OAuth2 and JWT for mobile app authentication and authorization"
      ]
    },
    {
      id: 3, company: "Excentus Corporation", location: "Dallas, TX",
      title: "Senior Software Engineer", period: "Dec 2013 – Mar 2019",
      achievements: [
        "Built Member Web Services (MWS) Interface — RESTful APIs consumed by iOS and Android mobile apps and external clients",
        "Developed push notification and SMS communication services for mobile member engagement",
        "Implemented RBAC-based authentication service for mobile app access control",
        "Created custom logging and caching layers (Log4j, Redis, EhCache) supporting high-performance mobile API responses"
      ]
    },
    {
      id: 4, company: "Citigroup", location: "Irving, TX",
      title: "Senior Developer Consultant", period: "June 2011 – April 2013",
      achievements: [
        "Designed and developed CITI's global product app backend for various mobile devices",
        "Built SMS 2-way messaging app for Payments Collection supporting mobile users",
        "Integrated RESTful APIs using Spring framework for mobile and web clients"
      ]
    },
    {
      id: 5, company: "USAA", location: "San Antonio, TX",
      title: "Senior Technical Lead", period: "April 2006 – June 2011",
      achievements: [
        "Developed push notification APIs for iOS and Android platforms — direct mobile development experience",
        "Built SMS 2-way messaging solutions for USAA mobile member services",
        "Maintained mobile.usaa.com and developed electronic fund transfer solutions for mobile banking",
        "Built event-based solutions for member activity tracking on mobile platforms"
      ]
    }
  ],

  projects: [
    {
      id: 1, title: "Connected Vehicle Mobile Platform — NissanConnect & Toyota/Lexus", company: "SiriusXM",
      description: "Architected and built the backend platform powering native mobile apps for Toyota, Lexus, and Nissan connected vehicle services. APIs supported vehicle owner registration, subscription management, remote access (engine start/stop, door lock/unlock), emergency services, and real-time vehicle health monitoring consumed directly by iOS and Android apps.",
      technologies: ["Java/Spring Boot", "Node.js/TypeScript", "REST APIs", "OAuth2", "JWT", "AWS Lambda", "DynamoDB", "Kinesis", "Push Notifications"],
      impact: "Handles 10M+ events per day supporting millions of connected vehicle mobile users",
      challenges: "Multiple OEM-specific mobile app contracts with no standardised API. Emergency services required sub-second response times.",
      decisions: "Designed a unified API contract layer abstracting OEM differences. Implemented circuit breakers to protect mobile clients from backend failures."
    },
    {
      id: 2, title: "Member Services Mobile API — Fuelrewards", company: "Excentus Corporation",
      description: "Built the Member Web Services (MWS) Interface — a RESTful API platform consumed by iOS and Android Fuelrewards apps. Supported member registration, rewards balance, status tier management, push notifications, and SMS communications for millions of loyalty program members.",
      technologies: ["Java", "Spring Boot", "REST APIs", "Oracle Responsys", "Redis", "Push Notifications", "SMS", "AWS"],
      impact: "Processed 5M+ mobile member communications monthly",
      challenges: "High-volume push notification spikes during promotions. Inconsistent partner data formats affecting mobile data display.",
      decisions: "Used async event-driven pipeline for notifications to prevent blocking mobile API responses. Redis caching for fast mobile data retrieval."
    },
    {
      id: 3, title: "Enterprise Platform Modernization — Mobile Backend", company: "Southwest Airlines",
      description: "Modernized Southwest Airlines' enterprise platforms including native mobile applications to cloud-native serverless architecture. Built and maintained Android-consumed backend APIs and shared SDKs using Kotlin-compatible REST standards. Implemented push notification services, real-time event processing, and secure mobile authentication patterns. Standardised OpenAPI contracts ensuring consistent mobile API experience across Android and iOS clients.",
      technologies: ["Kotlin (Android SDK)", "REST APIs", "AWS Lambda", "API Gateway", "DynamoDB", "Kafka", "Push Notifications (FCM)", "OAuth2", "JWT", "Terraform"],
      impact: "60% faster backend deployment velocity; mobile API response times improved by 40%; push notification delivery reliability improved to 99.9%; Android app reliability to 99.95% uptime",
      challenges: "Legacy monolith caused slow mobile API response times and cascading failures affecting Android app stability. Push notification delivery was unreliable under high load.",
      decisions: "Decoupled mobile-facing APIs from backend services using event-driven architecture. Adopted FCM for reliable push delivery. Standardised OpenAPI contracts for all Android-consumed endpoints ensuring type-safe Kotlin client generation."
    },
    {
      id: 4, title: "Core Mobile API Framework", company: "USAA",
      description: "Contributed to USAA's Core API Framework team building reusable APIs and shared libraries powering usaa.com, mobile.usaa.com, and native iOS/Android apps across Banking, Life Insurance, and Annuity lines of business.",
      technologies: ["Java/J2EE", "Spring Boot", "REST APIs", "Push Notifications", "IBM MQ", "WebSphere"],
      impact: "Mobile APIs serving millions of USAA members across Banking, Insurance, and Annuity apps",
      challenges: "Diverging API contracts between web and mobile channels causing backend duplication.",
      decisions: "Adopted single API contract strategy for multi-channel consumption. Standardised security interceptors across all mobile-facing APIs."
    }
  ],

  coreStrengths: [
    "Mobile API Design & Development",
    "Android Backend Integration (REST, Push, Auth)",
    "Cloud-Native Backend for Mobile (AWS)",
    "Event-Driven Architecture & Real-Time Processing",
    "Secure Mobile API Patterns (OAuth2, JWT)",
    "Push Notifications & SMS Services",
    "Performance Optimization for Mobile Clients",
    "Full-Stack Development & Leadership",
    "Observability & Reliability Engineering",
    "Technical Leadership & Mentorship"
  ],

  expertiseAreas: [
    { name: 'Android Development (Kotlin)', level: 72, icon: 'Code' },
    { name: 'Mobile API Design & REST Integration', level: 95, icon: 'Database' },
    { name: 'Push Notifications (FCM/APNs)', level: 90, icon: 'Zap' },
    { name: 'OAuth2 / JWT — Mobile Auth', level: 92, icon: 'Database' },
    { name: 'AWS & Cloud Backend for Mobile', level: 95, icon: 'Cloud' },
    { name: 'Java / Spring Boot / J2EE', level: 90, icon: 'Server' },
    { name: 'Node.js & TypeScript', level: 95, icon: 'Code' },
    { name: 'Microservices & Event-Driven Architecture', level: 95, icon: 'Server' },
    { name: 'System Design & Architecture', level: 95, icon: 'TrendingUp' },
    { name: 'SDK & Library Development', level: 90, icon: 'Code' },
    { name: 'Performance Optimization for Mobile', level: 88, icon: 'Zap' },
    { name: 'Team Leadership & Mentorship', level: 92, icon: 'Users' },
  ],

  skillSet: {
    "Android / Mobile — Transferable Skills": {
      items: ["REST API design for mobile clients — extensive experience", "Push notification services (iOS & Android) — built at USAA and Excentus", "OAuth2 / JWT authentication for mobile apps", "SDK and library development — shared internal SDKs at Southwest", "Event-driven real-time data for mobile — Kinesis/Kafka pipelines"]
    },
    "Backend / Application": {
      items: ["Java/J2EE", "Spring Framework", "Spring Boot", "Node.js/TypeScript", "Kotlin (learning)"]
    },
    "Mobile Technologies": {
      items: ["Android SDK (familiar)", "Kotlin", "Push Notifications (FCM/APNs)", "REST API integration", "OAuth2 / JWT for mobile auth"]
    },
    "Cloud / Serverless": {
      items: ["AWS Lambda", "API Gateway", "ECS Fargate", "DynamoDB", "SNS (Push)", "SQS", "Kinesis", "S3", "CloudFormation", "Terraform"]
    },
    "API & Integration": {
      items: ["REST", "OpenAPI / Swagger", "SOAP", "WebSockets (familiar)", "MuleSoft Anypoint"]
    },
    "Databases": {
      items: ["DynamoDB", "PostgreSQL", "Oracle", "MongoDB", "Redis"]
    },
    "Messaging & Event": {
      items: ["Apache Kafka", "AWS Kinesis", "SNS/SQS", "ActiveMQ", "RabbitMQ"]
    },
    "Security": {
      items: ["OAuth2", "JWT Authentication", "RBAC", "WS-Security", "Secrets management"]
    },
    "DevOps & Tooling": {
      items: ["Docker", "Kubernetes", "Terraform", "CloudFormation", "Git", "CI/CD", "Linux"]
    },
    "Testing": {
      items: ["TDD", "BDD", "Unit testing", "Integration testing", "JMeter"]
    }
  },

  tools: ["Git, GitLab, Bitbucket", "Docker", "Kubernetes", "Terraform", "Android Studio (familiar)", "IntelliJ/VSCode", "Splunk", "Linux"],
  databases: ["DynamoDB", "PostgreSQL", "Oracle", "MongoDB", "Redis"],
  scriptingLanguages: ["Shell scripting", "Python", "SQL", "Groovy"],
  methodologies: ["Agile (Sprint or Kanban)", "Waterfall"],
  appServers: ["Apache Tomcat", "JBoss", "IBM WebSphere"],
  operatingPlatforms: ["Linux", "Android", "Windows", "iOS", "Unix"],
  coreEnablingSkills: ["Communication (Verbal & Written)", "Collaboration/Teamwork", "Problem Solving", "Critical Thinking", "Adaptability/Flexibility", "Learning Agility", "Decision-Making", "Mentorship"],
  cognitiveEnablingSkills: ["Analytical Thinking", "Attention to Detail", "Judgment & Reasoning", "Research & Inquiry"],
  certifications: [{ id: 1, name: "AWS Solutions Architect - Associate", issuer: "Amazon Web Services", year: "2023" }],
  education: [
    { id: 1, degree: "Master's in Computer Applications", university: "Sri Venkateswara University, India", year: "2004" },
    { id: 2, degree: "Bachelor's in Computer Science", university: "Sri Venkateswara University, India", year: "2001" }
  ]
};
